import React, { useState, useEffect } from 'react';
import './SellCar.css';

import { Heading } from '../../components';

import { FaCar, FaComment, FaCog, FaCalendar, FaHeart, FaTachometerAlt, FaExclamationCircle, FaAngleDown } from 'react-icons/fa';
import { FaUser, FaEnvelope, FaPhoneAlt } from 'react-icons/fa';

import axios from "axios";

// API endpoint for handling form submissions
const API_PATH = 'https://primecars-react.asdesignsgalaxy.com/api/phpmailer/sellCar.php';

// Regular expressions for validating email and phone number formats
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const SellCar = () => {

  // State to store form input values
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    contactMethod: '',
    make: '',
    model: '',
    year: '',
    condition: '',
    mileage: '',
    accidents: '',
    comments: ''
  });

  // State to handle submission status and error messages
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Effect to reset the success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Handle form submission
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];

      // Validation checks for required fields
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.contactMethod) validationErrors.push('Contact method is required');
      if (!formData.make.trim()) validationErrors.push('Car make is required');
      if (!formData.model.trim()) validationErrors.push('Car model is required');
      if (!formData.year) validationErrors.push('Car year is required');
      if (!formData.condition) validationErrors.push('Condition is required');
      if (!formData.mileage) validationErrors.push('Mileage is required');
      if (!formData.accidents) validationErrors.push('Accident history is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Send form data to the server
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle server response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Submission failed');
      }

      // Reset form and display success message
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        contactMethod: '',
        make: '',
        model: '',
        year: '',
        condition: '',
        mileage: '',
        accidents: '',
        comments: ''
      });
    } catch (err) {
      // Handle errors
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      // Reset loading state
      setIsSubmitting(false);
    }
  };

  // Handle input field changes and update state
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="custom-form sell-car linear-bg">
      <div className="box-container">
        <div className="content">
          <form className='dark-form' onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={'Request a Car Sale Quote'} subTitle={'Sell Your Car'} sideHeading linear />

            <div className='input-box'>

              {/* Name field */}
              <div className='input-field'>
                <label htmlFor="name"><FaUser /></label>
                <input type="text" className='box' id="name" name="name" 
                  value={formData.name} onChange={handleInputChange} 
                  placeholder="Full Name" disabled={isSubmitting} required />
              </div>

              {/* Email field */}
              <div className='input-field'>
                <label htmlFor="email"><FaEnvelope /></label>
                <input type="email" className='box' id="email" name="email" 
                  value={formData.email} onChange={handleInputChange} 
                  placeholder="Email Address" disabled={isSubmitting} required />
              </div>

            </div>

            <div className='input-box'>

              {/* Phone Number field */}
              <div className='input-field'>
                <label htmlFor="number"><FaPhoneAlt /></label>
                <input type="tel" className='box' id="number" name="number" 
                  value={formData.number} onChange={handleInputChange} 
                  placeholder="Phone Number" disabled={isSubmitting} required />
              </div>

              {/* Contact Method field */}
              <div className='select'>
                <div className="select-container">
                  <label htmlFor="contactMethod"><FaCog /></label>
                  <select className='select-box' id="contactMethod" name="contactMethod" 
                    value={formData.contactMethod} onChange={handleInputChange} 
                    disabled={isSubmitting} required >
                    <option value="">Preferred Contact Method</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                  </select>
                  <div className='icon-container'><FaAngleDown className='icon' /></div>
                </div>
              </div>

            </div>

            <div className='input-box'>

              {/* Car Make field */}
              <div className='input-field'>
                <label htmlFor="make"><FaCar /></label>
                <input type="text" className='box' id="make" name="make" 
                  value={formData.make} onChange={handleInputChange} 
                  placeholder="Car Make" disabled={isSubmitting} required />
              </div>

              {/* Car Model field */}
              <div className='input-field'>
                <label htmlFor="model"><FaCar /></label>
                <input type="text" className='box' id="model" name="model" 
                  value={formData.model} onChange={handleInputChange} 
                  placeholder="Car Model" disabled={isSubmitting} required />
              </div>

            </div>

            <div className='input-box'>

              {/* Car Year fields */}
              <div className='input-field'>
                <label htmlFor="year"><FaCalendar /></label>
                <input type="text" className='box' id="year" name="year" 
                  value={formData.year} onChange={handleInputChange} 
                  placeholder="Car Year" disabled={isSubmitting} required />
              </div>

              {/* Car Condition fields */}
              <div className='select'>
                <div className="select-container">
                  <label htmlFor="condition"><FaHeart /></label>
                  <select className='select-box' id="condition" name="condition" 
                    value={formData.condition} onChange={handleInputChange} 
                    disabled={isSubmitting} required >
                    <option value="">Condition</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </select>
                  <div className='icon-container'><FaAngleDown className='icon' /></div>
                </div>
              </div>

            </div>

            <div className='input-box'>

              {/* Mileage field */}
              <div className='input-field'>
                <label htmlFor="mileage"><FaTachometerAlt /></label>
                <input type="number" className='box' id="mileage" name="mileage" 
                  value={formData.mileage} onChange={handleInputChange} 
                  placeholder="Mileage" disabled={isSubmitting} required />
              </div>

              {/* Accident History field */}
              <div className='select'>
                <div className="select-container">
                  <label htmlFor="accidents"><FaExclamationCircle /></label>
                  <select className='select-box' id="accidents" name="accidents" 
                    value={formData.accidents} onChange={handleInputChange} 
                    disabled={isSubmitting} required >
                    <option value="">Any Accidents?</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  <div className='icon-container'><FaAngleDown className='icon' /></div>
                </div>
              </div>

            </div>

            {/* Additional Comments field */}
            <div className='input-field text-area'>
              <label htmlFor="comments"><FaComment /></label>
              <textarea cols="30" rows="10" id="comments" className='box' 
                name="comments" value={formData.comments} onChange={handleInputChange} 
                placeholder="Additional Comments" disabled={isSubmitting} required></textarea>
            </div>

            {/* Submit button */}
            <button type="submit" className='btn' disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Now'}
            </button>

            {/* Error and success messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Request submitted</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default SellCar;